import { BaseMapInfoWindowBuilder } from "../../../../_shared/interfaces/maps/base-map-info-window-builder";

export type FuelChangesInfoWindowTranslate = (key: string) => string;

// Translation keys for the row labels, with the English copy used when no translate function is supplied.
const INFO_WINDOW_LABELS = {
  driver: { key: 'fuelDashboard.map.infoWindow.driver', fallback: 'Driver:' },
  vehicle: { key: 'fuelDashboard.map.infoWindow.vehicle', fallback: 'Vehicle:' },
  amount: { key: 'fuelDashboard.map.infoWindow.amount', fallback: 'Amount:' },
  tank: { key: 'fuelDashboard.map.infoWindow.tank', fallback: 'Tank:' },
  cost: { key: 'fuelDashboard.map.infoWindow.cost', fallback: 'Cost:' },
  location: { key: 'fuelDashboard.map.infoWindow.location', fallback: 'Location:' },
  latitude: { key: 'fuelDashboard.map.infoWindow.latitude', fallback: 'Latitude:' },
  longitude: { key: 'fuelDashboard.map.infoWindow.longitude', fallback: 'Longitude:' },
  timestamp: { key: 'fuelDashboard.map.infoWindow.timestamp', fallback: 'Timestamp:' },
  notAvailable: { key: 'fuelShared.charts.notAvailable', fallback: 'N/A' },
} as const;

export class FuelChangesMapInfoWindowBuilder extends BaseMapInfoWindowBuilder {
  private numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

  // The HTML is rebuilt on every marker click, so labels are resolved at build time and follow the active language.
  constructor(private readonly translate?: FuelChangesInfoWindowTranslate) {
    super();
  }

  private label(name: keyof typeof INFO_WINDOW_LABELS): string {
    const { key, fallback } = INFO_WINDOW_LABELS[name];
    return this.escapeHTML(this.translate ? this.translate(key) : fallback);
  }

  override buildMapInfoWindowHTML(data: any, currencySymbol: string = 'R'): string {

    const notAvailable = this.label('notAvailable');
    const signMultiplier = data.type === 'Fill-Up' ? 1 : -1;
    const vehicle = this.escapeHTML(data.vehicle);
    const driver = this.escapeHTML(data.driver);
    const amount = isFinite(Number(data.amount)) ? `${this.numberFormatter.format(Number(data.amount) * signMultiplier)} ℓ` : notAvailable;
    const tank = isFinite(Number(data.tank)) && data.tank > 0 ? this.numberFormatter.format(Number(data.tank)) : notAvailable
    const fuelConsumption = isFinite(Number(data.fuelConsumption)) ? `${this.numberFormatter.format(Number(data.fuelConsumption))} ℓ/100km` : notAvailable
    const cost = isFinite(Number(data.cost)) ? `${currencySymbol}${this.numberFormatter.format(Number(data.cost))}` : `${currencySymbol}0`;
    const location = this.escapeHTML(data.location);
    const timestamp = this.formatDateTime(data.timestamp);
    const lat = data.lat;
    const lng = data.lng;

    return `
        <div class="fc-iw">
          <style>
            .gm-style-iw-ch { 
              padding: 0 !important;
            }
    
            .gm-style-iw-d { 
              max-height: none !important;
              overflow: visible !important;
              overflow-x: hidden !important; 
            }
    
            .gm-style-iw, .gm-style-iw-c {
              max-width: none !important;
              overflow: visible !important;
              padding: 16px !important;
              height: 252px !important;
              width: 280px !important;
            }
    
            /* Rows layout: two columns (label + value), single-line, spaced vertically */
            .fc-iw .rows {
              display: grid;
              grid-template-columns: auto 1fr;
              column-gap: 12px;
              row-gap: 8px;
              align-items: start;
            }
    
            .fc-iw .label {
              color: #9FA1A4;
              font-weight: 400;
              align-self: start;
            }
    
            .fc-iw .val {
              align-self: start;
              overflow-wrap: anywhere;
              line-height: 1.25;
            }
          </style>
    
          <div class="rows">
            <div class="label">${this.label('driver')}</div>            <div class="val">${driver}</div>
            <div class="label">${this.label('vehicle')}</div>           <div class="val">${vehicle}</div>
            <div class="label">${this.label('amount')}</div>            <div class="val">${amount}</div>
            <div class="label">${this.label('tank')}</div>              <div class="val">${tank}</div>
            <!--<div class="label">Fuel Consumption:</div>  <div class="val">${fuelConsumption}</div>-->
            <div class="label">${this.label('cost')}</div>              <div class="val">${cost}</div>
            <div class="label">${this.label('location')}</div>          <div class="val">${location}</div>
            <div class="label">${this.label('latitude')}</div>          <div class="val">${lat}</div>
            <div class="label">${this.label('longitude')}</div>         <div class="val">${lng}</div>
            <div class="label">${this.label('timestamp')}</div>         <div class="val">${timestamp}</div>
          </div>
        </div>
     `;
  }
}